import React from 'react'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import { isEmpty } from 'lodash'
import queryString from 'query-string'
import { IconHistory, IconInfoCircle } from '@tabler/icons-react'

import { CopyButton } from '@/components/buttons/CopyButton'

const formatTime = (timestamp) =>
  new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

// A week back, not today: the point of the link is to come back to it later,
// after post-call processing has caught up, and a same-day window would have
// gone stale by then.
const EXPLORER_WINDOW_DAYS = 7

// Explorer hydrates its filters from the URL on load (`parseParams`), fetches
// the org's agents, then applies — so `agents` + `organizationId` is a real
// deep link rather than a hint. `agents` matches on user id, and
// `call_ingestion_worker` attributes every processed call to the voice agent's
// ai-agent user, which is what makes this the right id to send.
//
// Without that id there is nothing to filter on, and an unfiltered Explorer is
// still the right place to send someone.
const explorerLink = ({ agentUserId, organizationId } = {}) => {
  if (!agentUserId || !organizationId) {
    return '/explorer'
  }
  const endDate = new Date()
  const startDate = new Date(endDate)
  startDate.setDate(startDate.getDate() - EXPLORER_WINDOW_DAYS)
  return `/explorer?${queryString.stringify({
    organizationId,
    agents: agentUserId,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  })}`
}

// Calls made since this view was opened, and nothing more.
//
// The list lives in React state and dies with the view — no storage, no fetch.
// That is a deliberate limit rather than an unfinished feature: the post-call
// pipeline is the durable record, and keeping a second copy of call content
// here would be an unmanaged home for it. The notice below says so, because a
// list that silently empties itself reads as a bug.
const CallHistoryPanel = ({ history, selectedKey, onSelect, isCallLive, agentIdentity }) => (
  <aside className="voice-agents__call-history" aria-label="Calls this session">
    <header className="voice-agents__call-history-head">
      <IconHistory size={16} aria-hidden="true" />
      <span>History</span>
    </header>

    <div className="voice-agents__call-history-list" data-testid="call-history">
      {isCallLive && (
        <button
          type="button"
          className={classNames('voice-agents__call-history-item', {
            'voice-agents__call-history-item--active': selectedKey === null,
          })}
          onClick={() => onSelect(null)}
          data-testid="call-history-live"
        >
          <span className="voice-agents__call-history-live-dot" aria-hidden="true" />
          <span className="voice-agents__call-history-label">Live call</span>
        </button>
      )}

      {isEmpty(history) && !isCallLive ? (
        <p className="voice-agents__call-history-empty">
          Calls you make here will appear in this list.
        </p>
      ) : (
        history.map((call) => (
          <div
            key={call.key}
            className={classNames('voice-agents__call-history-item', {
              'voice-agents__call-history-item--active': selectedKey === call.key,
            })}
            data-testid="call-history-item"
          >
            <button
              type="button"
              className="voice-agents__call-history-open"
              onClick={() => onSelect(call.key)}
            >
              <span className="voice-agents__call-history-label">{formatTime(call.startedAt)}</span>
              {/* The Cartesia call id, verbatim and copyable. It is the only
                  thing that will still identify this call once the pipeline
                  has it, so it has to survive being read off a screen. */}
              <span className="voice-agents__call-history-id">{call.callId || 'no call id'}</span>
            </button>
            {call.callId && (
              <CopyButton
                content={call.callId}
                iconOnly
                text
                showTooltip
                tooltipContent="Copy call ID"
              />
            )}
          </div>
        ))
      )}
    </div>

    <div className="voice-agents__call-history-note" role="note">
      <IconInfoCircle size={14} aria-hidden="true" />
      <span>
        This list is kept only while you are in this view — leaving clears it. Calls show up in{' '}
        <Link to={explorerLink(agentIdentity)} data-testid="call-history-explorer-link">
          Call Explorer
        </Link>{' '}
        once post-call processing finishes.
      </span>
    </div>
  </aside>
)

export default CallHistoryPanel
